import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthContextType, Guest } from '../types/user';
import { 
  authAPI, 
  getCurrentSession, 
  getCurrentUser, 
  isDemoMode,
  // Import types from the API module which re-exports them
  type UserProfile,
  type AgeGroup,
  type UserRole
} from '../lib/auth/api';

// Define the types we need for this context
type SignUpData = {
  email: string;
  password: string;
  displayName?: string;
  role?: UserRole;
  ageGroup?: AgeGroup;
};

type SignInData = {
  email: string;
  password: string;
};

type OAuthProvider = 'google' | 'github';

// CraftyJoyUser matches our getUserProfile response
type CraftyJoyUser = {
  id: string;
  email?: string;
  email_confirmed_at?: string | null;
  profile?: UserProfile;
};

// Map Supabase error to legacy AuthError format for compatibility
interface AuthError {
  type: 'INVALID_CREDENTIALS' | 'USER_NOT_FOUND' | 'USER_EXISTS' | 'INVALID_TOKEN' | 'TOKEN_EXPIRED' | 'SUPABASE_ERROR';
  message: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Export the hook with a stable reference to avoid HMR issues
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export const SupabaseAuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | Guest | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authError, setAuthError] = useState<AuthError | null>(null);
  const [isDemo, setIsDemo] = useState(false);

  // Initialize auth state and check for existing session
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    const setupAuth = async () => {
      await initializeAuth();
      
      // Set up auth state change listener
      if (!isDemo) {
        try {
          const { supabase } = await import('../lib/auth/supabase');
          console.log('🔗 Setting up auth state change listener...');
          
          const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
            console.log('🔄 Auth state change event:', event, 'Session exists:', !!session, 'User ID:', session?.user?.id);
            setLoading(true);
            
            if (session?.user) {
              try {
                console.log('🔍 Fetching current user...');
                const currentUser = await getCurrentUser();
                console.log('📋 getCurrentUser result:', { 
                  hasUser: !!currentUser, 
                  hasProfile: !!(currentUser as CraftyJoyUser)?.profile,
                  userId: currentUser?.id,
                  profileData: (currentUser as CraftyJoyUser)?.profile,
                  userEmail: currentUser?.email
                });
                
                if (currentUser && currentUser.id) {
                  console.log('🔄 Converting user to CraftyJoy format...');
                  const craftyJoyUser = await convertSupabaseUserToCraftyJoyUser({
                    id: currentUser.id,
                    email: currentUser.email || session.user.email,
                    email_confirmed_at: session.user.email_confirmed_at,
                    profile: (currentUser as CraftyJoyUser)?.profile || undefined
                  });
                  
                  console.log('✅ User conversion successful:', {
                    userId: craftyJoyUser.id,
                    userType: craftyJoyUser.type,
                    needsRoleSelection: 'needsRoleSelection' in craftyJoyUser ? craftyJoyUser.needsRoleSelection : false,
                    hasProfile: !!(currentUser as CraftyJoyUser)?.profile
                  });
                  
                  setUser(craftyJoyUser);
                  setIsAuthenticated(true);
                }
                else {
                  console.log('❌ No valid user data found');
                  setUser(null);
                  setIsAuthenticated(false);
                }
              } catch (error) {
                console.error('❌ Error in auth state change handler:', error);
                setUser(null);
                setIsAuthenticated(false);
              }
            } else {
              console.log('🚪 User signed out or no session');
              setUser(null);
              setIsAuthenticated(false);
            }
            
            setLoading(false);
          });
          
          unsubscribe = () => subscription.unsubscribe();
          console.log('✅ Auth state change listener set up successfully');
        } catch (error) {
          console.error('❌ Failed to set up auth state change listener:', error);
        }
      }
    };

    setupAuth();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [isDemo]);

  const initializeAuth = async () => {
    try {
      setLoading(true);
      
      // Check if we're in demo mode
      const demoMode = isDemoMode();
      setIsDemo(demoMode);
      
      if (demoMode) {
        // In demo mode, check for demo user in localStorage
        const demoUser = localStorage.getItem('craftyjoy_demo_user');
        if (demoUser) {
          const parsedUser = JSON.parse(demoUser);
          setUser(parsedUser);
          setIsAuthenticated(parsedUser.type !== 'guest');
        }
        setLoading(false);
        return;
      }

      // Check for existing Supabase session
      console.log('🔍 Checking for existing session...');
      const session = await getCurrentSession();
      console.log('📋 Session check result:', { hasSession: !!session, userId: session?.user?.id });
      
      if (session?.user) {
        console.log('🔍 Found existing session, fetching user...');
        const currentUser = await getCurrentUser();
        console.log('📋 Current user result:', { 
          hasUser: !!currentUser, 
          userId: currentUser?.id,
          hasProfile: !!(currentUser as CraftyJoyUser)?.profile
        });
        
        if (currentUser) {
          console.log('🔄 Converting existing user to CraftyJoy format...');
          // Convert Supabase user to CraftyJoy user format
          const craftyJoyUser = await convertSupabaseUserToCraftyJoyUser(currentUser);
          console.log('✅ Existing user converted successfully:', { 
            userId: craftyJoyUser.id, 
            userType: craftyJoyUser.type 
          });
          setUser(craftyJoyUser);
          setIsAuthenticated(true);
        }
      } else {
        console.log('ℹ️ No existing session found');
      }
      // OAuth callbacks are handled by AuthCallbackPage component
    } catch (error) {
      console.error('Auth initialization error:', error);
      setAuthError(mapSupabaseError(error));
    } finally {
      setLoading(false);
    }
  };

  const convertSupabaseUserToCraftyJoyUser = async (supabaseUser: CraftyJoyUser): Promise<User> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const profile = supabaseUser.profile as any; // Type assertion for now until we have proper DB types
    
    console.log('🔄 Converting Supabase user:', {
      userId: supabaseUser.id,
      email: supabaseUser.email,
      hasProfile: !!profile,
      profileStatus: profile?.status,
      profileRole: profile?.role
    });
    
    // If no profile exists (e.g., fresh OAuth sign-up), create a basic user
    if (!profile) {
      console.log('⚠️ No profile found, creating basic user for:', supabaseUser.id);
      const basicUser = {
        id: supabaseUser.id,
        type: 'student' as const, // Default type
        name: supabaseUser.email?.split('@')[0] || 'User',
        email: supabaseUser.email,
        createdAt: new Date().toISOString(),
        classrooms: [],
        achievements: [],
        skillLevel: 'beginner' as const,
        age: 10, // Default age
        needsRoleSelection: true, // Flag to indicate role selection is needed
      };
      console.log('✅ Created basic user:', basicUser);
      return basicUser;
    }

    // Check if user needs role selection (OAuth users with pending status)
    const needsRoleSelection = profile.status === 'pending_role_selection';
    
    console.log('🔍 Profile analysis:', {
      status: profile.status,
      role: profile.role,
      needsRoleSelection,
      displayName: profile.display_name,
      username: profile.username
    });

    // Map Supabase profile to CraftyJoy user format
    const baseUser = {
      id: profile.id || supabaseUser.id,
      name: profile.display_name || profile.username || supabaseUser.email || 'Unknown',
      email: supabaseUser.email,
      avatarUrl: profile.avatar_url,
      createdAt: profile.created_at || new Date().toISOString(),
      updatedAt: profile.updated_at || new Date().toISOString(),
      isVerified: supabaseUser.email_confirmed_at != null,
    };

    // Create type-specific user based on role
    switch (profile.role) {
      case 'student':
        return {
          ...baseUser,
          type: 'student',
          age: getAgeFromGroup(profile.age_group),
          grade: profile.preferences?.grade || undefined,
          classrooms: [], // TODO: Load from relationships
          achievements: [], // TODO: Load from user_achievements
          skillLevel: profile.preferences?.skillLevel || 'beginner',
          needsRoleSelection, // Add the role selection flag
        } as User;

      case 'parent':
        return {
          ...baseUser,
          type: 'parent',
          children: [], // TODO: Load from relationships
          subscriptionTier: profile.preferences?.subscriptionTier || 'free',
          parentalControls: profile.preferences?.parentalControls || {
            contentFiltering: 'moderate',
            allowPublicSharing: false,
            approvalRequired: true,
            activityReports: 'weekly',
          },
        } as User;

      case 'teacher':
        return {
          ...baseUser,
          type: 'teacher',
          school: profile.preferences?.school || undefined,
          classrooms: [], // TODO: Load from relationships
          verificationStatus: profile.preferences?.verificationStatus || 'pending',
          subjects: profile.preferences?.subjects || [],
          gradesTaught: profile.preferences?.gradesTaught || [],
        } as User;

      case 'creator':
        return {
          ...baseUser,
          type: 'creator',
          bio: profile.bio || undefined,
          templates: [], // TODO: Load from projects
          earnings: 0, // TODO: Load from earnings table
          followerCount: 0, // TODO: Load from relationships
          verificationStatus: profile.preferences?.verificationStatus || 'pending',
          specialties: profile.preferences?.specialties || [],
          submittedTemplates: [], // TODO: Load from projects
        } as User;

      default:
        // Default to student
        return {
          ...baseUser,
          type: 'student',
          age: getAgeFromGroup(profile.age_group),
          classrooms: [],
          achievements: [],
          skillLevel: 'beginner',
        } as User;
    }
  };

  const getAgeFromGroup = (ageGroup: string): number => {
    const ageMap: Record<string, number> = {
      '6-8': 7,
      '9-11': 10,
      '12-14': 13,
      '15-17': 16,
      '18+': 18,
    };
    return ageMap[ageGroup] || 10;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapSupabaseError = (error: any): AuthError => {
    if (!error) return { type: 'SUPABASE_ERROR', message: 'Unknown error' };
    
    const message = error.message || 'An unexpected error occurred';
    
    if (message.includes('Invalid login credentials')) {
      return { type: 'INVALID_CREDENTIALS', message: 'Invalid email or password' };
    }
    
    if (message.includes('User not found')) {
      return { type: 'USER_NOT_FOUND', message: 'No account found with this email' };
    }
    
    if (message.includes('User already registered')) {
      return { type: 'USER_EXISTS', message: 'An account with this email already exists' };
    }
    
    return { type: 'SUPABASE_ERROR', message };
  };

  // Auth methods
  const showAuthModal = () => {
    setAuthError(null);
    setIsAuthModalOpen(true);
  };

  const hideAuthModal = () => {
    setAuthError(null);
    setIsAuthModalOpen(false);
  };

  const login = async (email: string, password: string): Promise<void> => {
    try {
      setAuthError(null);
      
      if (isDemo) {
        throw new Error('Cannot login in demo mode. Please configure Supabase.');
      }

      const signInData: SignInData = { email, password };
      const result = await authAPI.signin(signInData);

      if (result.error) {
        setAuthError(mapSupabaseError(result.error));
        throw new Error(result.error.message);
      }

      if (result.user) {
        const craftyJoyUser = await convertSupabaseUserToCraftyJoyUser(result.user);
        setUser(craftyJoyUser);
        setIsAuthenticated(true);
        hideAuthModal();
      }
    } catch (error) {
      const authError = mapSupabaseError(error);
      setAuthError(authError);
      throw error;
    }
  };

  const register = async (userData: {
    email: string;
    password: string;
    fullName: string;
    userType: 'child' | 'parent' | 'teacher' | 'creator';
  }): Promise<void> => {
    try {
      setAuthError(null);
      
      if (isDemo) {
        throw new Error('Cannot register in demo mode. Please configure Supabase.');
      }

      // Map userType to role (child -> student)
      const role = userData.userType === 'child' ? 'student' : userData.userType;
      
      const signUpData: SignUpData = {
        email: userData.email,
        password: userData.password,
        displayName: userData.fullName,
        role: role as UserRole,
        ageGroup: userData.userType === 'child' ? '9-11' : '18+',
      };

      const result = await authAPI.signup(signUpData);

      if (result.error) {
        setAuthError(mapSupabaseError(result.error));
        throw new Error(result.error.message);
      }

      if (result.user) {
        const craftyJoyUser = await convertSupabaseUserToCraftyJoyUser(result.user);
        setUser(craftyJoyUser);
        setIsAuthenticated(true);
        hideAuthModal();
      }
    } catch (error) {
      const authError = mapSupabaseError(error);
      setAuthError(authError);
      throw error;
    }
  };

  const logout = async () => {
    try {
      if (!isDemo) {
        await authAPI.signout();
      }
      
      setUser(null);
      setIsAuthenticated(false);
      
      // Clear any cached data
      localStorage.removeItem('craftyjoy_demo_user');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const updateUser = async (data: Partial<User>) => {
    if (!user || !isAuthenticated) return;

    try {
      if (!isDemo) {
        // Update profile in Supabase
        const result = await authAPI.updateProfile(user.id, {
          displayName: data.name,
          bio: 'bio' in data ? data.bio : undefined,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          preferences: data as any, // Store additional data in preferences
        });

        if (result.error) {
          setAuthError(mapSupabaseError(result.error));
          return;
        }
      }

      // Update local state
      const updatedUser = { ...user, ...data } as User;
      setUser(updatedUser);
      
      if (isDemo) {
        localStorage.setItem('craftyjoy_demo_user', JSON.stringify(updatedUser));
      }
      
      hideAuthModal();
    } catch (error) {
      setAuthError(mapSupabaseError(error));
    }
  };

  const setDemoUser = (demoUser: User | Guest) => {
    setUser(demoUser);
    setIsAuthenticated(demoUser.type !== 'guest');
    localStorage.setItem('craftyjoy_demo_user', JSON.stringify(demoUser));
  };

  const createGuestSession = () => {
    const guestUser: Guest = {
      id: `guest_${Date.now()}`,
      name: 'Guest',
      type: 'guest',
      createdAt: new Date().toISOString(),
      allowedFeatures: {
        canCreateProjects: true,
        canUseBasicAI: false,      // Guests cannot use AI features
        canSaveLocally: false,     // Guests cannot save
        maxProjects: 1,            // Only one project allowed
        maxBlocks: 50,
        sessionDuration: 30,       // 30 minutes session
      },
    };
    setUser(guestUser);
    setIsAuthenticated(true); // allow access to editor for guests
    if (isDemo) {
      localStorage.setItem('craftyjoy_demo_user', JSON.stringify(guestUser));
    }
  };

  const switchToChild = (childId: string) => {
    // TODO: Implement child switching for parent accounts
    console.log('Switching to child:', childId);
  };

  const sendMagicLink = async (email: string): Promise<void> => {
    try {
      setAuthError(null);
      
      if (isDemo) {
        throw new Error('Magic links not available in demo mode. Please configure Supabase.');
      }

      const result = await authAPI.resetPassword({ email });
      
      if (result.error) {
        setAuthError(mapSupabaseError(result.error));
        throw new Error(result.error.message);
      }
    } catch (error) {
      const authError = mapSupabaseError(error);
      setAuthError(authError);
      throw error;
    }
  };

  const checkUserExists = async (email: string): Promise<boolean> => {
    if (isDemo) return false;
    
    try {
      const result = await authAPI.checkEmailAvailability(email);
      return !result.available; // If not available, user exists
    } catch (error) {
      console.error('Error checking user existence:', error);
      return false;
    }
  };

  const signInWithOAuth = async (provider: OAuthProvider) => {
    try {
      setAuthError(null);
      
      if (isDemo) {
        throw new Error('OAuth not available in demo mode. Please configure Supabase.');
      }

      const result = await authAPI.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          scopes: provider === 'github' ? 'read:user user:email' : undefined,
        },
      });

      if (result.error) {
        setAuthError(mapSupabaseError(result.error));
        throw new Error(result.error.message);
      }

      // OAuth will redirect, so no need to handle response here
    } catch (error) {
      const authError = mapSupabaseError(error);
      setAuthError(authError);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    loading,
    isAuthModalOpen,
    authError,
    showAuthModal,
    hideAuthModal,
    login,
    logout,
    updateUser,
    createGuestSession,
    switchToChild,
    register,
    sendMagicLink,
    checkUserExists,
    setDemoUser,
    signInWithOAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {/* Show demo mode indicator */}
      {isDemo && (
        <div className="fixed top-0 left-0 right-0 bg-yellow-500 text-black text-center py-1 text-sm z-50">
          Demo Mode: Configure Supabase for real authentication
        </div>
      )}
    </AuthContext.Provider>
  );
};